import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  makeStyles,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { useApp } from '@backstage/core-plugin-api';
import {
  TechDocsAddonLocations,
  createTechDocsAddonExtension,
} from '@backstage/plugin-techdocs-react';
import { techdocsPlugin } from '@backstage/plugin-techdocs';

import { useTierNavigation } from './useTierNavigation';
import { TierDefinition } from './tiers/types';

/** Tabs read well up to this many tiers; beyond it a dropdown scales better. */
const MAX_TABS = 4;

const useStyles = makeStyles(theme => ({
  tabs: {
    marginLeft: theme.spacing(2),
    minHeight: theme.spacing(6),
    color: theme.page.fontColor,
  },
  tab: {
    minWidth: theme.spacing(9),
    minHeight: theme.spacing(6),
    color: alpha(theme.page.fontColor, 0.7),
    '&.Mui-selected': {
      color: theme.page.fontColor,
    },
  },
  indicator: {
    backgroundColor: theme.page.fontColor,
  },
  tabLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  formControl: {
    minWidth: 120,
    marginLeft: theme.spacing(2),
  },
  label: {
    color: theme.page.fontColor,
    '&.Mui-focused': {
      color: theme.page.fontColor,
    },
  },
  select: {
    color: theme.page.fontColor,
    '&:before': {
      borderBottomColor: alpha(theme.page.fontColor, 0.5),
    },
    '&:hover:not(.Mui-disabled):before': {
      borderBottomColor: theme.page.fontColor,
    },
    '&:after': {
      borderBottomColor: theme.page.fontColor,
    },
    '& .MuiSelect-icon': {
      color: theme.page.fontColor,
    },
  },
}));

/**
 * Display order for the tier controls: the optional `priority` hint wins
 * (ascending), declaration order otherwise (Array.prototype.sort is stable).
 */
function sortForDisplay(tiers: TierDefinition[]): TierDefinition[] {
  return [...tiers].sort(
    (a, b) =>
      (a.priority ?? Number.POSITIVE_INFINITY) -
      (b.priority ?? Number.POSITIVE_INFINITY),
  );
}

/**
 * Header-location TechDocs addon (Story 6.3) for switching documentation
 * tiers. The tier list comes from the entity's catalog annotations via the
 * 6.2 parser (declared contract, ADR T5) — never from a blob manifest.
 * Adaptive per AC 2: 2-4 tiers render as tabs, 5+ as a dropdown, and a single
 * resolved tier (including the implicit `internal` of un-annotated entities)
 * or a parse failure renders nothing — fail closed, never a broken selector.
 * Navigation only, not access control: every authenticated Hub user sees
 * every tier.
 */
function TierSelectorAddon(): JSX.Element | null {
  const classes = useStyles();
  const app = useApp();
  const { loading, tiers, currentTier, switchTo } = useTierNavigation();

  if (loading || tiers.length < 2 || !currentTier) {
    return null;
  }

  const ordered = sortForDisplay(tiers);

  const labelFor = (tier: TierDefinition) => {
    const Icon = tier.icon ? app.getSystemIcon(tier.icon) : undefined;
    if (!Icon) {
      return tier.label;
    }
    return (
      <span className={classes.tabLabel}>
        <Icon fontSize="small" /> {tier.label}
      </span>
    );
  };

  const handleSwitch = (tierId: string) => {
    void switchTo(tierId);
  };

  if (ordered.length <= MAX_TABS) {
    return (
      <Tabs
        className={classes.tabs}
        classes={{ indicator: classes.indicator }}
        value={currentTier}
        onChange={(_event, value) => handleSwitch(String(value))}
        aria-label="Documentation tier"
      >
        {ordered.map(tier => (
          <Tab
            key={tier.id}
            className={classes.tab}
            value={tier.id}
            label={labelFor(tier)}
            title={tier.description}
          />
        ))}
      </Tabs>
    );
  }

  return (
    <FormControl className={classes.formControl} size="small">
      <InputLabel className={classes.label} id="techdocs-tier-selector-label">
        Tier
      </InputLabel>
      <Select
        className={classes.select}
        labelId="techdocs-tier-selector-label"
        value={currentTier}
        onChange={event => handleSwitch(String(event.target.value))}
      >
        {ordered.map(tier => (
          <MenuItem key={tier.id} value={tier.id}>
            {labelFor(tier)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export const TierSelector = techdocsPlugin.provide(
  createTechDocsAddonExtension({
    name: 'TierSelector',
    location: TechDocsAddonLocations.Header,
    component: TierSelectorAddon,
  }),
);
